import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { CustomerSummary } from '../../../types/api';

/**
 * Fetches a single customer.
 *
 * The server's GET /customers/:uuid controller wraps the row in a
 * `{ customer }` envelope (see `customers.controller.ts:getCustomerController`).
 * After `apiClient.get` strips the outer `{ success, data }` envelope, the
 * `data` we receive here is `{ customer: CustomerSummary }`. We unwrap that
 * inner key so consumers get the customer record directly — otherwise every
 * `customer.<field>` access would return undefined and the detail page would
 * render with blank fields.
 */
export function useCustomerDetail(uuid: string) {
  return useQuery({
    queryKey: queryKeys.customers.detail(uuid),
    queryFn: async () => {
      const res = await apiClient.get<{ customer: CustomerSummary }>(
        endpoints.customers.detail(uuid),
      );
      return res.data.customer;
    },
    staleTime: 60_000,
    enabled: Boolean(uuid),
  });
}
